import { CreateClaimProvider, CreateClaimProviderType, SupportProvider } from '../../../../api';
import { Formik, FormikProps } from 'formik';
import { observer } from 'mobx-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ClaimEditorStore } from '../../../../stores/claim-editor';
import * as Yup from 'yup';
import * as S from './additional-providers-form.styles';
import * as CS from '../section.styles';
import { BaseButton, BaseInput } from '../../../base';
import { Field } from 'formik';
import { AsyncSelectField } from '../../../base/form/async-select/async-select';
import { FormErrorBanner } from '../../../base/form/form-error-banner';
import { FormField } from '../../../form-field';

interface ProviderFormValues {
    providerLookup?: string;
    first: string;
    last: string;
    npi: string;
    providerId?: string;
}

const ProviderSchema = Yup.object().shape({
    first: Yup.string().required('First name is required'),
    last: Yup.string().required('Last name is required'),
    npi: Yup.string().required('NPI is required').max(10, 'NPI must be 10 characters or less'),
});

const initialValues: ProviderFormValues = {
    first: '',
    last: '',
    npi: '',
    providerId: undefined,
};

interface IAdditionalProvidersFormProps {
    claimEditorStore: ClaimEditorStore;
}

export const AdditionalProvidersForm: React.FunctionComponent<IAdditionalProvidersFormProps> = observer(props => {
    const { claimEditorStore } = props;

    const handleAddProvider = useCallback((type: CreateClaimProviderType) => {
        const currentProviders = claimEditorStore?.providers || [];
        const newProvider: CreateClaimProvider = {
            first: undefined,
            middle: undefined,
            last: undefined,
            suffix: undefined,
            npi: undefined,
            providerId: undefined,
            type,
        };
        claimEditorStore?.updateProviders([...currentProviders, newProvider]);
    }, [claimEditorStore]);

    const handleDeleteProvider = useCallback((index: number) => {
        const currentProviders = claimEditorStore?.providers || [];
        const updatedProviders = currentProviders.filter((_, i) => i !== index);
        claimEditorStore?.updateProviders(updatedProviders);
    }, [claimEditorStore]);

    const handleUpdateProvider = useCallback((index: number, provider: CreateClaimProvider) => {
        const currentProviders = claimEditorStore?.providers || [];
        const updatedProviders = [...currentProviders];
        updatedProviders[index] = provider;
        claimEditorStore?.updateProviders(updatedProviders);
    }, [claimEditorStore]);

    const providers = claimEditorStore?.providers || [];
    const hasRendering = providers.some(p => p.type === CreateClaimProviderType.RENDERING);
    const hasReferring = providers.some(p => p.type === CreateClaimProviderType.REFERRING);
    const hasSupervising = providers.some(p => p.type === CreateClaimProviderType.SUPERVISING);

    return (
        <S.FormContainer>
            <CS.ButtonContainer>
                <BaseButton 
                    variant="primary" 
                    onClick={() => handleAddProvider(CreateClaimProviderType.RENDERING)} 
                    disabled={hasRendering}
                >
                    + Add Rendering
                </BaseButton>
                <BaseButton 
                    variant="primary" 
                    onClick={() => handleAddProvider(CreateClaimProviderType.REFERRING)} 
                    disabled={hasReferring}
                >
                    + Add Referring
                </BaseButton>
                <BaseButton 
                    variant="primary" 
                    onClick={() => handleAddProvider(CreateClaimProviderType.SUPERVISING)} 
                    disabled={hasSupervising}
                >
                    + Add Supervising
                </BaseButton>
            </CS.ButtonContainer>

            {providers.map((provider, index) => (
                <ProviderSection
                    key={`${provider.type}-${index}`}
                    providerType={provider.type}
                    title={provider.type}
                    existingProvider={provider}
                    index={index}
                    onDelete={handleDeleteProvider}
                    onUpdateProvider={handleUpdateProvider}
                    claimEditorStore={claimEditorStore}
                />
            ))}
        </S.FormContainer>
    );
});

interface ProviderSectionProps {
    providerType: CreateClaimProviderType;
    title: string;
    existingProvider: CreateClaimProvider;
    index: number;
    onDelete: (index: number) => void;
    onUpdateProvider: (index: number, provider: CreateClaimProvider) => void;
    claimEditorStore: ClaimEditorStore;
}

const ProviderSection: React.FunctionComponent<ProviderSectionProps> = observer(
    ({ providerType, title, index, onDelete, onUpdateProvider, existingProvider, claimEditorStore }) => {
        const [isSaving, setIsSaving] = useState(false);
        const saveTimerRef = useRef<NodeJS.Timeout | undefined>(undefined);
        const isMountedRef = useRef(false);

        const getInitialValues = useCallback((): ProviderFormValues => {
            if (existingProvider) {
                return {
                    providerLookup: [existingProvider.first, existingProvider.last].filter(Boolean).join(' ') || '',
                    first: existingProvider.first || '',
                    last: existingProvider.last || '',
                    npi: existingProvider.npi || '',
                    providerId: (existingProvider.providerId as any) || undefined,
                };
            }
            return { ...initialValues, providerLookup: '' };
        }, [existingProvider]);

        const convertToProvider = (values: ProviderFormValues): CreateClaimProvider => {
            return {
                first: values.first || undefined,
                middle: existingProvider.middle || undefined,
                last: values.last || undefined,
                suffix: existingProvider.suffix || undefined,
                npi: values.npi || undefined,
                providerId: (values.providerId as any) || existingProvider.providerId || undefined,
                type: providerType,
            };
        };

        const debouncedSave = useCallback(
            (values: ProviderFormValues) => {
                if (saveTimerRef.current) {
                    clearTimeout(saveTimerRef.current);
                }

                setIsSaving(true);
                saveTimerRef.current = setTimeout(() => {
                    const providerData = convertToProvider(values);
                    onUpdateProvider(index, providerData);
                    setIsSaving(false);
                }, 500);
            },
            [onUpdateProvider, index],
        );

        const handleProviderSelect = useCallback(
            (provider: SupportProvider, formik: FormikProps<ProviderFormValues>) => {
                const displayName = [provider.first, provider.last].filter(Boolean).join(' ') || provider.organization || '';
                const newValues: ProviderFormValues = {
                    providerLookup: displayName,
                    first: provider.first || '',
                    last: provider.last || '',
                    npi: provider.npi || '',
                    providerId: (provider.id as any) || undefined,
                };

                formik.setValues(newValues);
            },
            [],
        );

        useEffect(() => {
            return () => {
                if (saveTimerRef.current) {
                    clearTimeout(saveTimerRef.current);
                }
            };
        }, []);

        return (
            <CS.SectionContainer>
                <CS.SectionHeader>
                    <CS.SectionTitle>{title}</CS.SectionTitle>
                    <BaseButton variant="warning" onClick={() => onDelete(index)}>✕ Delete {title} Provider</BaseButton>
                </CS.SectionHeader>

                <Formik
                    initialValues={getInitialValues()}
                    validationSchema={ProviderSchema}
                    onSubmit={() => {
                        // No-op, we auto-save
                    }}
                    enableReinitialize
                    validateOnMount={false}
                    validateOnChange={true}
                    validateOnBlur={false}
                >
                    {formik => {
                        useEffect(() => {
                            if (!isMountedRef.current) {
                                isMountedRef.current = true;
                                return;
                            }
                            debouncedSave(formik.values);
                        }, [formik.values, debouncedSave]);

                        useEffect(() => {
                            if (claimEditorStore.validationSignal > 0) {
                                formik.submitForm();
                            }
                        }, [claimEditorStore.validationSignal]);

                        return (
                            <>
                                <FormErrorBanner submitCount={formik.submitCount} isValid={formik.isValid} errors={formik.errors} />
                                <S.FormGrid>
                                    <S.FormField span={3}>
                                        <S.Label>{providerType.charAt(0) + providerType.slice(1).toLowerCase()} Provider Lookup</S.Label>
                                        <Field
                                            name="providerLookup"
                                            component={AsyncSelectField}
                                            placeholder="Type last name to search"
                                            loadOptions={async (q: string) => {
                                                const results: SupportProvider[] = await claimEditorStore.searchProvidersByLastName(q, providerType);
                                                return results.map(p => ({
                                                    label: [p.first, p.last].filter(Boolean).join(' ') || (p as any).organization || '',
                                                    value: p.id as any,
                                                    provider: p,
                                                    info: p.npi ? ' | ' + p.npi : undefined,
                                                } as any));
                                            }}
                                            onChange={(opt: any) => {
                                                const p: SupportProvider = opt?.provider;
                                                if (!p) return;
                                                handleProviderSelect(p, formik);
                                            }}
                                        />
                                    </S.FormField>

                                    <S.FormField>
                                        <FormField label="First Name" required>
                                            <BaseInput
                                                type="text"
                                                name="first"
                                                value={formik.values.first}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                errorMessage={formik.touched.first && formik.errors.first ? formik.errors.first : undefined}
                                            />
                                        </FormField>
                                    </S.FormField>

                                    <S.FormField>
                                        <FormField label="Last Name" required>
                                            <BaseInput
                                                type="text"
                                                name="last"
                                                value={formik.values.last}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                errorMessage={formik.touched.last && formik.errors.last ? formik.errors.last : undefined}
                                            />
                                        </FormField>
                                    </S.FormField>

                                    <S.FormField>
                                        <FormField label="NPI" required>
                                            <BaseInput
                                                type="text"
                                                name="npi"
                                                value={formik.values.npi}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                maxLength={10}
                                                errorMessage={formik.touched.npi && formik.errors.npi ? formik.errors.npi : undefined}
                                            />
                                        </FormField>
                                    </S.FormField>
                                </S.FormGrid>
                            </>
                        );
                    }}
                </Formik>
            </CS.SectionContainer>
        );
    },
);

